import React, { useEffect, useState } from "react";
import TabEmplacement from "./Tabs/TabEmplacement";
import TabRestaurant from "./Tabs/TabRestaurant";
import TabTenant from "./Tabs/TabTenant";
import TabWebTime from "./Tabs/TabWebTime";
import CreateLocationModal from "./Modal/CreateLocationModal";
import Autocomplete from "@mui/material/Autocomplete";
import dayjs from "dayjs";
import {
  Box,
  TextField,
  FormControl,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import "../../css/Emplacement.css";

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`location-tabpanel-${index}`}
      aria-labelledby={`location-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `location-tab-${index}`,
    "aria-controls": `location-tabpanel-${index}`,
  };
}

function LocationView() {
  const { t } = useTranslation(["Location"]);
  const userState = useSelector((state) => state.user);

  const [restaurants, setRestaurants] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [currentRestaurant, setCurrentRestaurant] = useState(null);

  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [regions, setRegions] = useState([]);

  const [openModal, setOpenModal] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  const [alert, setAlert] = useState({
    show: false,
    type: "success",
    message: "",
  });

  useEffect(() => {
    const currentId = localStorage.getItem("currentRestaurantId");
    const savedTab = localStorage.getItem("tabValue");

    const init = async () => {
      await getCountriesProvincesRegions();
      const data = await getEmplacement();

      if (currentId) {
        const resto = data.find((r) => r.noEmplacement == currentId);
        if (resto) {
          setRestaurant(resto.nom);
          setCurrentRestaurant(resto);
        }
        localStorage.removeItem("currentRestaurantId");
      }

      if (savedTab) {
        setTabValue(parseInt(savedTab, 10));
        localStorage.removeItem("tabValue");
      }
    };

    init();
  }, []);

  const getCountriesProvincesRegions = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Localization/GetProvince`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );
      const data = await response.json();

      const uniqueCountries = [];
      const uniqueProvinces = [];
      const uniqueRegions = [];

      data.forEach((element) => {
        if (!uniqueCountries.find((c) => c.nomFr === element.paysFr))
          uniqueCountries.push({
            id: uniqueCountries.length + 1,
            nomFr: element.paysFr,
            nomEn: element.paysEn,
          });
        if (!uniqueProvinces.find((p) => p.nomFr === element.provinceFr))
          uniqueProvinces.push({
            id: uniqueProvinces.length + 1,
            nomFr: element.provinceFr,
            nomEn: element.provinceEn,
          });
        if (!uniqueRegions.find((r) => r.nomFr === element.regionFr))
          uniqueRegions.push({
            id: uniqueRegions.length + 1,
            nomFr: element.regionFr,
            nomEn: element.regionEn,
          });
      });

      setCountries(uniqueCountries);
      setProvinces(uniqueProvinces);
      setRegions(uniqueRegions);
    } catch (error) {
      console.error("Error fetching location data:", error);
      setAlert({ show: true, type: "error", message: t("ErrorFetchingData") });
    } finally {
      setIsLoading(false);
    }
  };

  const getEmplacement = async (showInactiveValue = false) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/Location/GetEmplacement?showInactive=${showInactiveValue}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userState.token}`,
        },
      },
    );
    const data = await response.json();
    setRestaurants(data);
    return data;
  };

  const handleChangeEmplacement = (event, newValue) => {
    if (newValue) {
      const selectedRestaurant = restaurants.find((r) => r.nom === newValue);
      setCurrentRestaurant(selectedRestaurant);
      setRestaurant(newValue);
    } else {
      setCurrentRestaurant(null);
      setRestaurant(null);
    }
  };

  const handleChangeToggleButton = async () => {
    const newValue = !showInactive;

    setShowInactive(newValue);

    const data = await getEmplacement(newValue);

    // Si le restaurant sélectionné existe encore
    if (currentRestaurant) {
      const resto = data.find(
        (r) => r.noEmplacement === currentRestaurant.noEmplacement,
      );

      setCurrentRestaurant(resto || null);
    }
  };

  // Fonction pour formater une date en jj-mm-aaaa
  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return null;
    const [day, month, year] = dateString.split("-");
    if (!day || !month || !year) return null;
    return `${day.padStart(2, "0")}-${month.padStart(2, "0")}-${year}`;
  };

  const saveLocationData = async (data, userToken, activeTab) => {
    let locationData = {
      noEmplacement: Number(data.noEmplacement) || 0,
      noAdresse: data.noAdresse || 0,
      activeTab,
    };

    if (activeTab === 0) {
      locationData = {
        ...locationData,
        IdDirecteurOps:
          data.directeurOps?.noPersonne ?? data.directeurOps ?? null,
        IdEmployePrincipal:
          data.responsableCoach?.noPersonne ?? data.responsableCoach ?? null,
        nom: data.nameLocation || "",
        email: data.email || null,
        noTelPrincipal: data.phoneNumber || null,
        dateOuverture: formatDateToDDMMYYYY(data.dateOuverture),
        dateFermeture: formatDateToDDMMYYYY(data.dateFermeture),
        adresseEmplacement: {
          adresse: data.address || "",
          ville: data.city || "",
          codePostal: data.zipCode || "",
          nomRegionFr: data.region?.nomFr || "",
          nomProvinceFr: data.province?.nomFr || "",
          nomPaysFr: data.country?.nomFr || "",
          nomRegionEn: data.region?.nomEn || "",
          nomProvinceEn: data.province?.nomEn || "",
          nomPaysEn: data.country?.nomEn || "",
        },
      };
    }

    if (activeTab === 1) {
      locationData = {
        ...locationData,
        nbSiege: data.nbSiege,
        superficie: data.superficie,
        ipAddress: data.IpAddress,
        idLibro: data.idLibro?.trim() || null,
        prixLivraison: data.PrixLivraison,
        prixLivraisonParPalette: data.PrixLivraisonParPalette,
        idTypePaiement: data.IdTypePaiement,
        nomTypePaiementFr: data.NomTypePaiementFr,
        location: data.location,
      };
    }

    if (activeTab === 2) {
      locationData = {
        ...locationData,
        IdBailleur: data.id || null,
        NomBailleur: data.lessorName || null,
        AdresseBailleur: data.lessorAddress || null,
        TelAffaireBailleur: data.lessorBusinessPhone || null,
        FaxBailleur: data.lessorFax || null,
        MailAffaireBailleur: data.lessorBusinessEmail || null,
        ContactBailleur: data.lessorContact || null,
        DateBailEffectif: data.leaseEffectiveDate || null,
        DateBailExpiration: data.leaseExpirationDate || null,
        OptionRenouvelerBailleur: data.lessorRenewalOption || null,
        PeriodeAvisBailleur: data.lessorNoticePeriod || null,
      };
    }

    if (activeTab === 3) {
      locationData = {
        ...locationData,
        monday_open: data.monday_open
          ? dayjs(data.monday_open).format("HH:mm:ss")
          : null,
        tuesday_open: data.tuesday_open
          ? dayjs(data.tuesday_open).format("HH:mm:ss")
          : null,
        wednesday_open: data.wednesday_open
          ? dayjs(data.wednesday_open).format("HH:mm:ss")
          : null,
        thursday_open: data.thursday_open
          ? dayjs(data.thursday_open).format("HH:mm:ss")
          : null,
        friday_open: data.friday_open
          ? dayjs(data.friday_open).format("HH:mm:ss")
          : null,
        saturday_open: data.saturday_open
          ? dayjs(data.saturday_open).format("HH:mm:ss")
          : null,
        sunday_open: data.sunday_open
          ? dayjs(data.sunday_open).format("HH:mm:ss")
          : null,

        monday_close: data.monday_close
          ? dayjs(data.monday_close).format("HH:mm:ss")
          : null,
        tuesday_close: data.tuesday_close
          ? dayjs(data.tuesday_close).format("HH:mm:ss")
          : null,
        wednesday_close: data.wednesday_close
          ? dayjs(data.wednesday_close).format("HH:mm:ss")
          : null,
        thursday_close: data.thursday_close
          ? dayjs(data.thursday_close).format("HH:mm:ss")
          : null,
        friday_close: data.friday_close
          ? dayjs(data.friday_close).format("HH:mm:ss")
          : null,
        saturday_close: data.saturday_close
          ? dayjs(data.saturday_close).format("HH:mm:ss")
          : null,
        sunday_close: data.sunday_close
          ? dayjs(data.sunday_close).format("HH:mm:ss")
          : null,

        delivery: data.delivery ?? false,
        takeout: data.takeout ?? false,
        dinein: data.dinein ?? false,
        terrace: data.terrace ?? false,
        meeting_room: data.meetingRoom ?? false,
      };
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Location/SaveLocation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
          body: JSON.stringify(locationData),
        },
      );

      const result = await response.json();

      if (response.ok) {
        setAlert({
          show: true,
          type: "success",
          message: "Location saved successfully!",
        });

        setTimeout(() => {
          localStorage.setItem("currentRestaurantId", data.noEmplacement);
          localStorage.setItem("tabValue", activeTab);
          window.location.reload();
        }, 1500);
      } else {
        setAlert({
          show: true,
          type: "error",
          message: result.title || "Error saving location",
        });
      }
    } catch (error) {
      console.error(error);
      setAlert({
        show: true,
        type: "error",
        message: "Error connecting to server",
      });
    }
  };

  const handleTabChange = (event, newValue) => setTabValue(newValue);
  const handleCloseAlert = () => setAlert((prev) => ({ ...prev, show: false }));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          onClick={() => setOpenModal(true)}
          sx={{ textTransform: "none" }}
        >
          {t("NewLocation")}
        </Button>
        <ToggleButtonGroup
          color="primary"
          value={showInactive}
          exclusive
          onChange={handleChangeToggleButton}
        >
          <ToggleButton value={true}>{t("ShowInactiveLocation")}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <CreateLocationModal
        open={openModal}
        handleClose={() => setOpenModal(false)}
      />

      <Box sx={{ minWidth: 120, mb: 3 }}>
        <FormControl fullWidth>
          <Autocomplete
            options={restaurants.map((r) => r.nom)}
            onChange={handleChangeEmplacement}
            value={restaurant || null}
            renderInput={(params) => (
              <TextField {...params} label={t("Location")} />
            )}
          />
        </FormControl>
      </Box>

      <Box>
        {!isLoading && countries && provinces && regions ? (
          <Box sx={{ backgroundColor: "white", borderRadius: 1, p: 3 }}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Tabs value={tabValue} onChange={handleTabChange} centered>
                <Tab label={t("Location")} {...a11yProps(0)} />
                <Tab label={t("Restaurant")} {...a11yProps(1)} />
                <Tab label={t("Tenant")} {...a11yProps(2)} />
                <Tab label={t("WebTime")} {...a11yProps(3)} />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              <TabEmplacement
                emplacement={currentRestaurant}
                countries={countries}
                provinces={provinces}
                regions={regions}
                onSave={saveLocationData}
              />
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              <TabRestaurant
                emplacement={currentRestaurant}
                onSave={saveLocationData}
              />
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              <TabTenant
                emplacement={currentRestaurant}
                onSave={saveLocationData}
              />
            </TabPanel>
            <TabPanel value={tabValue} index={3}>
              <TabWebTime
                emplacement={currentRestaurant}
                onSave={saveLocationData}
              />
            </TabPanel>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>

      <Snackbar
        open={alert.show}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.type}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LocationView;
